import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { TenantHead } from '@/components/TenantHead';

interface TenantContextType {
  username: string | null;
  userId: string | null;
  isCustomDomain: boolean;
  isSubdomain: boolean;
  loading: boolean;
  notFound: boolean;
}

const TenantContext = createContext<TenantContextType>({
  username: null,
  userId: null,
  isCustomDomain: false,
  isSubdomain: false,
  loading: true,
  notFound: false,
});

export const useTenant = () => useContext(TenantContext);

function detectTenant(): { username: string | null; isSubdomain: boolean; isCustomDomain: boolean } {
  const hostname = window.location.hostname;

  // Local development or preview environments — no subdomain routing
  if (hostname === 'localhost' || hostname.includes('lovable.app') || hostname.includes('lovableproject.com') || hostname.includes('vercel.app')) {
    return { username: null, isSubdomain: false, isCustomDomain: false };
  }

  const parts = hostname.split('.');

  const RESERVED_SUBDOMAINS = ['www', 'admin', 'api', 'mail', 'ftp'];

  // e.g. samuel.criaaa.com → parts = ['samuel', 'criaaa', 'com']
  const isSubdomain = parts.length === 3 && parts[1] === 'criaaa' && parts[2] === 'com' && !RESERVED_SUBDOMAINS.includes(parts[0]);
  if (isSubdomain) {
    return { username: parts[0], isSubdomain: true, isCustomDomain: false };
  }

  // www.criaaa.com or other reserved subdomains → treat as root
  const isReservedSubdomain = parts.length === 3 && parts[1] === 'criaaa' && parts[2] === 'com' && RESERVED_SUBDOMAINS.includes(parts[0]);
  if (isReservedSubdomain) {
    return { username: null, isSubdomain: false, isCustomDomain: false };
  }

  // criaaa.com root → no tenant
  const isRoot = parts.length === 2 && parts[0] === 'criaaa' && parts[1] === 'com';
  if (isRoot) {
    return { username: null, isSubdomain: false, isCustomDomain: false };
  }

  // Any other domain → custom domain lookup
  return { username: null, isSubdomain: false, isCustomDomain: true };
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const detected = detectTenant();
  const [username, setUsername] = useState<string | null>(detected.username);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(detected.isCustomDomain); // only loading if custom domain needs lookup
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!detected.isCustomDomain) return;

    const hostname = window.location.hostname.toLowerCase().replace(/^www\./, '');
    console.debug('[TenantContext] resolving custom domain', { hostname });
    (async () => {
      // Use the security-definer RPC which checks status = 'active'
      const { data: rpcResult, error: rpcError } = await supabase
        .rpc('resolve_custom_domain', { p_domain: hostname });

      console.debug('[TenantContext] resolve_custom_domain result', { hostname, rpcResult, rpcError });

      let resolvedUsername: string | null = null;

      if (!rpcError && rpcResult && rpcResult.length > 0) {
        resolvedUsername = rpcResult[0].username;
        setUsername(resolvedUsername);
      } else {
        // Fallback: direct view query (case-insensitive)
        const { data: profile, error: viewError } = await supabase
          .from('public_profiles')
          .select('username')
          .ilike('custom_domain', hostname)
          .maybeSingle();

        console.debug('[TenantContext] fallback public_profiles result', { hostname, profile, viewError });

        if (profile?.username) {
          resolvedUsername = profile.username;
          setUsername(resolvedUsername);
        } else {
          setNotFound(true);
        }
      }

      if (resolvedUsername) {
        const { data: profileData } = await supabase
          .from('public_profiles')
          .select('user_id')
          .eq('username', resolvedUsername)
          .maybeSingle();
        if (profileData?.user_id) setUserId(profileData.user_id);
      }

      setLoading(false);
    })();
  }, [detected.isCustomDomain]);

  // Resolve userId for subdomains (username already known from detectTenant)
  useEffect(() => {
    if (!detected.isSubdomain || !detected.username) return;
    (async () => {
      const { data } = await supabase
        .from('public_profiles')
        .select('user_id')
        .eq('username', detected.username)
        .maybeSingle();
      if (data?.user_id) setUserId(data.user_id);
    })();
  }, []);

  return (
    <TenantContext.Provider value={{
      username,
      userId,
      isCustomDomain: detected.isCustomDomain,
      isSubdomain: detected.isSubdomain,
      loading,
      notFound,
    }}>
      <TenantHead />
      {children}
    </TenantContext.Provider>
  );
}
